import axios, { AxiosInstance, AxiosResponse } from "axios";
// import { response } from "express";
import {
  LoginResponse,
  MapOnRiderResponse,
  TopZyppRidersResponse,
  HotRoutesResponse,
  CityWiseSplitResponse,
  RiderEvAndDeliveriesResponse,
  CarbonSavedResponse,
  BarChartResponse,
  NewsCardInfoResponse,
  TickerDataResponse,
  NotificationAlert,
  NotificationResponse,
  FleetGrownJourneyResponse,
} from "../@Types/DRO/ApiResponses";

export type ClientMappingStatus = "PENDING" | "APPROVED" | "REJECTED" | "SKIPPED";
export type UpdateClientMappingStatusAction = "APPROVE" | "REJECT";

export interface FetchClientMappingDashboardItem {
  category: string;
  id: number;
  createdAt: string;
  createdTime: string; // "DD/MM/YYYY" — use directly for Date column
  userName: string | null;
  merchantName: string; // → Client Selected column
  clientId: string;
  address: string | null;
  documentId: number | null; // null = no document; build image URL from this
  rewardAmount: number;
  status: ClientMappingStatus;
}

export interface FetchClientMappingDashboardResponse {
  code: number;
  message: string;
  data: FetchClientMappingDashboardItem[];
  count: number; // total record count for pagination
}

export interface UpdateClientMappingStatusResponse {
  code: number;
  message: string;
}

export type UpdateClientMappingClientIdResponse = UpdateClientMappingStatusResponse;

export const isSuccessfulClientMappingResponse = (code: number): boolean =>
  code === 200 || code === 2000;

export interface RentalOwnershipHeader {
  key: string;
  label: string;
  type: string;
}

export interface RentalOwnershipAction {
  key: string;
  label: string;
}

export interface RentalOwnershipRecord {
  userId: number;
  userName: string;
  ebNumber: string;
  fleetType: number | string;
  rideStartDate: string;
  totalRideDays: number;
  earningDays: number;
  targetDays: number;
  totalEarning: number;
  actions: RentalOwnershipAction[];
}

export interface RentalOwnershipDashboardResponse {
  code: number;
  message: string;
  data: {
    pagination: {
      currentPage: number;
      limit: number;
      totalPages: number;
      totalRecords: number;
    };
    headers: RentalOwnershipHeader[];
    records: RentalOwnershipRecord[];
  };
}

export interface RentalOwnershipActionResponse {
  code: number;
  message: string;
}

const BASE_URL = process.env.REACT_APP_SERVER_URL;

export class ClientApi {
  public static api = new ClientApi();
  private instance: AxiosInstance;
  static googleMapsOnDutyRiders() {
    throw new Error("Method not implemented.");
  }
  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  }
  formatDate = (date: Date | null): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Adjust month to be 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  onlineUserAuthentication(
    username: string,
    password: string
  ): Promise<AxiosResponse<LoginResponse>> {
    return axios.post<LoginResponse>(`https://stag-micro.bcykal.com/mobycy/zypp-client-backend/login`, {
      username,
      password,
    });
  }
  getToken() {
    return localStorage.getItem("access_token");
  }

  fetchRentalOwnershipDashboard(
    search: string,
    fromDate: string | null,
    toDate: string | null,
    page: number,
    limit: number
  ): Promise<RentalOwnershipDashboardResponse> {
    const params: Record<string, string | number | undefined> = {
      search: search || undefined,
      page,
      limit,
    };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    return this.instance
      .get<RentalOwnershipDashboardResponse>("api/rental-ownership/dashboard", {
        params,
        headers: { Authorization: `Bearer ${this.getToken()}` },
      })
      .then((response) => response.data);
  }

  updateRentalOwnership(
    userId: number,
    type: "ACCEPT" | "REJECT"
  ): Promise<RentalOwnershipActionResponse> {
    return this.instance
      .post<RentalOwnershipActionResponse>("api/rental-ownership/accept", {
        userId: String(userId),
        type,
      }, {
        headers: { Authorization: `Bearer ${this.getToken()}` },
      })
      .then((response) => response.data);
  }

  exportRentalOwnership(): Promise<Blob> {
    return this.instance
      .get<Blob>("api/rental-ownership/export", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${this.getToken()}` },
      })
      .then((response) => response.data);
  }

  googleMapsOnDutyRiders(): Promise<MapOnRiderResponse[]> {
    return this.instance
      .get<MapOnRiderResponse[]>("get/onDuty/riders", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  carbonSavedData(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<CarbonSavedResponse> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/data/carbon-saved";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
    return this.instance
      .get<CarbonSavedResponse>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  treeSavedData(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<CarbonSavedResponse[]> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/data/trees-saved";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
    return this.instance
      .get<CarbonSavedResponse[]>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }

  deliveriesDoneData(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<CarbonSavedResponse[]> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/data/total-deliveries";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }

    return this.instance
      .get<CarbonSavedResponse[]>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }

  riderEvAndTotalDeleveries(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<RiderEvAndDeliveriesResponse> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/data/allotted-riders";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
    return this.instance
      .get<RiderEvAndDeliveriesResponse>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  barChartApi(): Promise<BarChartResponse[]> {
    return this.instance
      .get<BarChartResponse[]>("get/rider-rideAge-data", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  newsCardInfo(): Promise<NewsCardInfoResponse> {
    return this.instance
      .get<NewsCardInfoResponse>("get/city/data/news", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  notificationList(): Promise<NotificationResponse> {
    return this.instance
      .get<NotificationResponse>("notifications", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  notificationalert(): Promise<NotificationAlert> {
    return this.instance
      .get<NotificationAlert>("notification/alert", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  topZyppRiders(): Promise<TopZyppRidersResponse> {
    return this.instance
      .get<TopZyppRidersResponse>("get/data/top-riders", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }

  hotRoutes(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<HotRoutesResponse> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/data/hot-route";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
    return this.instance
      .get<HotRoutesResponse>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }

  cityWiseSplit(
    startDate: Date | null,
    endDate: Date | null
  ): Promise<CityWiseSplitResponse> {
    const formattedStartDate = startDate ? this.formatDate(startDate) : null;
    const formattedEndDate = endDate ? this.formatDate(endDate) : null;
    let apiUrl = "get/city/data/city-order-density";
    if (endDate) {
      apiUrl += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
    return this.instance
      .get<CityWiseSplitResponse>(apiUrl, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }

  tickerData(token: string | null): Promise<TickerDataResponse> {
    return axios
      .get<TickerDataResponse>(`${BASE_URL}get/data/ticker`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  fleetGrownJourney(): Promise<FleetGrownJourneyResponse> {
    return this.instance
      .get<FleetGrownJourneyResponse>("get/data/fleet-growth/ev", {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      })
      .then((e) => {
        return e.data;
      });
  }
  fleetGrownJourneyDelivery(): Promise<FleetGrownJourneyResponse> {
    return this.instance
      .get<FleetGrownJourneyResponse>(
        "get/data/fleet-growth/deliveries",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      )
      .then((e) => {
        return e.data;
      });
  }

  async fetchClientMappingDashboard(
    token: string | null,
    page: number, // 0-indexed
    size: number, // always 10
    fromDate: string, // "YYYY-MM-DD"
    toDate: string, // "YYYY-MM-DD"
    search: string,
    status?: string, // "PENDING" | "APPROVED" | "REJECTED" | "SKIPPED"
    category?: string
  ): Promise<FetchClientMappingDashboardResponse> {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      fromDate,
      toDate,
      search,
    });
    if (status) {
      params.append("status", status);
    }
    if (category) {
      params.append("category", category);
    }
    const response = await fetch(
      `${BASE_URL}api/client-mapping/dashboard?${params}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.message || "Failed to fetch client report");
    }
    return payload;
  }
async downloadClientMappingReport(
  token: string | null,
  fromDate: string,
  toDate: string,
  status?: string,
  category?: string
): Promise<Blob> {
  const params = new URLSearchParams();

  params.append("status", status || "");
  params.append("fromDate", fromDate);
  params.append("toDate", toDate);
  if (category) {
    params.append("category", category);
  }

  const response = await fetch(
    `${BASE_URL}api/client-mapping/export?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to download client mapping report");
  }

  return response.blob();
}
  async updateClientMappingStatus(
    token: string | null,
    action: UpdateClientMappingStatusAction, // "APPROVE" or "REJECT"
    mappingId: number
  ): Promise<UpdateClientMappingStatusResponse> {
    const params = new URLSearchParams({
      action,
      mappingId: String(mappingId),
    });
    const response = await fetch(
      `${BASE_URL}api/client-mapping/update-status?${params}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.message || "Failed to update client status");
    }
    return payload;
  }

  async updateClientMappingClientId(
    token: string | null,
    mappingId: number,
    clientId: string
  ): Promise<UpdateClientMappingClientIdResponse> {
    const response = await fetch(
      `${BASE_URL}api/client-mapping/update/client-id`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mappingId,
          clientId,
        }),
      }
    );
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.message || "Failed to update client id");
    }
    return payload;
  }
}
